import { ChevronLeft, MoreHorizontal } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useIsPhone } from '../lib/useMediaQuery'
import { Button } from './Button'
import { useLink } from './context'
import { actionsToMenu, DropdownMenu } from './DropdownMenu'
import { IconButton } from './IconButton'
import { Tabs, type TabsProps } from './Tabs'
import type { ActionDef } from './types'

export interface Crumb {
  label: string
  href?: string
}

/** Pfad zur aktuellen Seite: „Lager / Bestand“. Der letzte Eintrag ist die Seite selbst. */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  const Link = useLink()
  if (items.length === 0) return null
  return (
    <nav aria-label="Pfad" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-2 text-dense">
        {items.map((item, index) => {
          const last = index === items.length - 1
          return (
            <Fragment key={`${item.label}-${index}`}>
              {index > 0 && (
                <li aria-hidden className="text-fg-disabled">
                  /
                </li>
              )}
              <li
                className={cn('min-w-0', last ? 'truncate-line font-medium text-fg' : 'shrink-0 text-fg-muted')}
                aria-current={last ? 'page' : undefined}
              >
                {item.href && !last ? (
                  <Link href={item.href} className="rounded-xs hit-area hover:text-fg">
                    {item.label}
                  </Link>
                ) : (
                  item.label
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}

export interface PageHeaderProps {
  title: string
  /** Zähler, Belegnummer in Mono */
  titleSuffix?: ReactNode
  /** stehen am Desktop in der Topbar (dann `breadcrumbsPlacement="none"`), sonst über dem Titel */
  breadcrumbs?: Crumb[]
  breadcrumbsPlacement?: 'above' | 'none'
  /** mobil und in Detailseiten */
  back?: { label: string; href: string }
  /** <StatusPill> */
  status?: ReactNode
  /** eine Zeile unter dem Titel: Lieferant · Standort · Datum */
  meta?: ReactNode
  primaryAction?: ActionDef
  /** höchstens zwei sichtbar */
  secondaryActions?: ActionDef[]
  /** „···“-Menü */
  moreActions?: ActionDef[]
  tabs?: TabsProps<string>
  /** Standard true bei Editoren */
  sticky?: boolean
  className?: string
}

const run = (action: ActionDef) => () => void action.onSelect()

/** Kopf einer Seite: Titel, höchstens eine Hauptaktion rechts, davor bis zu zwei Nebenaktionen, der Rest unter „···“. */
export function PageHeader({
  title,
  titleSuffix,
  breadcrumbs,
  breadcrumbsPlacement = 'above',
  back,
  status,
  meta,
  primaryAction,
  secondaryActions = [],
  moreActions = [],
  tabs,
  sticky,
  className,
}: PageHeaderProps) {
  const Link = useLink()
  const phone = useIsPhone()
  // Auf dem Telefon wandert die Hauptaktion in die feste Leiste unten; Nebenaktionen fallen in „···“.
  const visibleSecondary = phone ? [] : secondaryActions.slice(0, 2)
  const overflow = [...(phone ? secondaryActions : secondaryActions.slice(2)), ...moreActions]

  return (
    <header
      className={cn(
        'flex flex-col bg-surface',
        sticky && 'sticky top-0 z-(--z-sticky) border-b border-line',
        className,
      )}
      data-ui="page-header"
    >
      <div className="flex flex-col gap-1 px-(--page-px) pt-4 pb-3 md:pt-5">
        {back ? (
          <Link
            href={back.href}
            className="-ml-1 inline-flex min-h-6 w-fit items-center gap-0.5 rounded-xs text-dense text-fg-muted hover:text-fg touch:min-h-11"
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} aria-hidden />
            {back.label}
          </Link>
        ) : (
          breadcrumbs && breadcrumbsPlacement === 'above' && <Breadcrumbs items={breadcrumbs} className="mb-0.5" />
        )}
        <div className={cn('flex min-h-(--control-h) flex-wrap gap-x-3 gap-y-2', phone ? 'items-start' : 'items-center')}>
          {phone ? (
            // Telefon: Der Titel bricht auf zwei Zeilen um statt abgeschnitten zu werden („Augustiner Lagerbier
            // Hell 20×0,5“); Kennung und Status stehen in der Zeile darunter.
            <div className="flex min-w-0 flex-1 flex-col gap-1" data-ui="page-header-title">
              <h1 className="line-clamp-2 text-heading break-words text-fg" title={title}>
                {title}
              </h1>
              {(titleSuffix !== undefined || status) && (
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1" data-ui="page-header-sub">
                  {titleSuffix !== undefined && <span className="min-w-0 text-body text-fg-muted">{titleSuffix}</span>}
                  {status && <span className="shrink-0">{status}</span>}
                </div>
              )}
            </div>
          ) : (
            // Titel so breit wie sein Text (höchstens die Zeile): Passen Titel und Aktionen nicht nebeneinander, rücken die
            // Aktionen in die nächste Zeile – statt den Titel auf 0 px zu drücken (Tablet hoch: „Bestand“, „Rechnungen“)
            <div className="flex max-w-full min-w-0 grow basis-auto items-baseline gap-3" data-ui="page-header-title">
              <h1 className="truncate-line text-heading text-fg" title={title}>
                {title}
              </h1>
              {titleSuffix !== undefined && <span className="shrink-0 text-body text-fg-muted">{titleSuffix}</span>}
              {status && <span className="shrink-0 self-center">{status}</span>}
            </div>
          )}
          <div className="ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2" data-print="hide" data-ui="page-header-actions">
            {visibleSecondary.map((action) => (
              <Button
                key={action.id}
                variant={action.variant ?? 'secondary'}
                icon={action.icon}
                hotkey={action.hotkey}
                disabled={action.disabled}
                disabledReason={action.disabledReason}
                onClick={run(action)}
              >
                {action.label}
              </Button>
            ))}
            {overflow.length > 0 && (
              <DropdownMenu
                align="end"
                trigger={<IconButton icon={MoreHorizontal} label="Weitere Aktionen" variant="secondary" />}
                items={actionsToMenu(overflow)}
              />
            )}
            {primaryAction && !phone && (
              <Button
                variant="primary"
                icon={primaryAction.icon}
                hotkey={primaryAction.hotkey}
                disabled={primaryAction.disabled}
                disabledReason={primaryAction.disabledReason}
                onClick={run(primaryAction)}
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
        {meta && <div className="text-dense text-fg-muted">{meta}</div>}
      </div>
      {tabs && <Tabs {...tabs} className={cn('px-(--page-px)', tabs.className)} />}
      {primaryAction && phone && (
        <div
          className="fixed inset-x-0 bottom-0 z-(--z-shell) border-t border-line bg-surface px-(--page-px) pt-3 pb-safe-bar"
          data-print="hide"
          data-density="touch"
          data-ui="page-primary-bar"
        >
          <Button
            variant="primary"
            size="lg"
            fullWidth
            icon={primaryAction.icon}
            disabled={primaryAction.disabled}
            disabledReason={primaryAction.disabledReason}
            onClick={run(primaryAction)}
          >
            {primaryAction.label}
          </Button>
        </div>
      )}
    </header>
  )
}
